/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Cpu, Settings2, Loader2, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useLocalAI } from '@/hooks/useLocalAI';
import { LOCAL_AI_PROVIDERS, LOCAL_AI_STATUS_LABELS } from '@/lib/localLLM';

/**
 * LocalAIStatusBadge — indicatore visivo dello stato del modello locale
 * ("Modello pronto", "In elaborazione…", "Caricamento 45%") con popover di
 * configurazione (provider Ollama / WebLLM / regole).
 */
const STATUS_STYLE = {
  idle: { dot: 'bg-muted-foreground', Icon: Cpu },
  loading: { dot: 'bg-warning', Icon: Loader2 },
  ready: { dot: 'bg-success', Icon: CheckCircle2 },
  processing: { dot: 'bg-primary', Icon: Sparkles },
  error: { dot: 'bg-destructive', Icon: AlertTriangle },
};

const PROVIDER_SHORT = { ollama: 'Ollama', webllm: 'WebLLM', rules: 'Regole' };

export const LocalAIStatusBadge = ({ compact = false, className }) => {
  const ai = useLocalAI();
  const [open, setOpen] = useState(false);
  const cfg = ai.engine.getConfig();
  const [draft, setDraft] = useState(cfg);

  const style = STATUS_STYLE[ai.status] || STATUS_STYLE.idle;
  const Icon = style.Icon;
  const label = ai.status === 'loading' && ai.progress > 0
    ? `Caricamento ${Math.round(ai.progress * 100)}%`
    : LOCAL_AI_STATUS_LABELS[ai.status] || ai.status;

  const apply = () => {
    ai.engine.setConfig(draft);
    ai.engine.init();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(ai.engine.getConfig()); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-xl border border-border/70 bg-card px-2.5 py-1.5 text-xs font-medium',
            'hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors',
            className,
          )}
          data-testid="local-ai-status-badge"
          aria-label={`Stato AI locale: ${label}`}
        >
          <span className="relative flex h-2.5 w-2.5">
            {(ai.status === 'processing' || ai.status === 'loading') && (
              <span className={cn('absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping', style.dot)} />
            )}
            <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', style.dot)} />
          </span>
          <Icon className={cn('h-3.5 w-3.5', (ai.status === 'loading' || ai.status === 'processing') && 'animate-spin')} />
          {!compact && <span data-testid="local-ai-status-label">{label}</span>}
          {ai.provider && !compact && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">· {PROVIDER_SHORT[ai.provider]}</span>
          )}
          <Settings2 className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 glass-strong rounded-xl" data-testid="local-ai-config-popover">
        <div className="space-y-3">
          <div>
            <div className="font-display font-semibold text-sm flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> AI locale (zero cloud)</div>
            <p className="text-xs text-muted-foreground mt-1">
              Il testo non lascia mai il dispositivo. Stato: <b>{label}</b>
              {ai.model && <> · modello <span className="font-mono">{ai.model}</span></>}
              {ai.lastLatencyMs != null && <> · {ai.lastLatencyMs} ms</>}
            </p>
            {ai.status === 'loading' && <Progress value={Math.round(ai.progress * 100)} className="h-1.5 mt-2" />}
            {ai.error && <p className="text-[11px] text-warning mt-1">Fallback attivo: {ai.error}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Provider</Label>
            <Select value={draft.provider} onValueChange={(v) => setDraft({ ...draft, provider: v })}>
              <SelectTrigger data-testid="local-ai-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(LOCAL_AI_PROVIDERS).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(draft.provider === 'ollama' || draft.provider === 'auto') && (
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">URL Ollama</Label>
                <Input value={draft.ollamaUrl} onChange={(e) => setDraft({ ...draft, ollamaUrl: e.target.value })} className="h-9 font-mono text-xs" data-testid="local-ai-ollama-url" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Modello Ollama</Label>
                <Input value={draft.ollamaModel} onChange={(e) => setDraft({ ...draft, ollamaModel: e.target.value })} className="h-9 font-mono text-xs" placeholder="llama3.2:3b · phi3:mini" data-testid="local-ai-ollama-model" />
              </div>
            </div>
          )}
          {(draft.provider === 'webllm' || draft.provider === 'auto') && (
            <div>
              <Label className="text-xs">Modello WebLLM (WebGPU)</Label>
              <Input value={draft.webllmModel} onChange={(e) => setDraft({ ...draft, webllmModel: e.target.value })} className="h-9 font-mono text-xs" data-testid="local-ai-webllm-model" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button size="sm" onClick={apply} data-testid="local-ai-apply-btn">Applica e ricarica</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LocalAIStatusBadge;
